import React, { useState, useEffect, useCallback } from "react";
import { getSunbedSessionLogs, SunbedSessionLogApiItem } from "../../services/sunbeds";
import { SunbedSessionLogsTable, SunbedLog } from "./SunbedSessionLogsTable";
import { SunbedActionToolbar } from "./SunbedActionToolbar";
import { PaginationFooter } from "./PaginationFooter";

export const SunbedSessionLogs = () => {
  const [logs, setLogs] = useState<SunbedLog[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);

  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getSunbedSessionLogs(searchTerm);

      const mappedLogs: SunbedLog[] = ((response as any)?.data || []).map((log: any) => ({
        id: Math.random().toString(36).substr(2, 9), // Generate temp ID as API doesn't provide one
        customer: log.customer,
        sunbedDetails: log.sunbed_details,
        actualPackageMins: log.actual_package_mins,
        sessionDuration: log.session_duration,
        createdBy: log.created_by,
        status: log.status
      }));

      setLogs(mappedLogs);
    } catch (error) {
      console.error("Failed to fetch sunbed logs:", error);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchLogs();
    }, 500);
    return () => clearTimeout(timer);
  }, [fetchLogs]);

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedLogs = logs.slice(indexOfFirstItem, indexOfLastItem);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="flex flex-col space-y-6 overflow-hidden flex-1 h-full">
      <SunbedActionToolbar
        totalItems={logs.length}
        showingStart={paginatedLogs.length > 0 ? indexOfFirstItem + 1 : 0}
        showingEnd={Math.min(indexOfLastItem, logs.length)}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search logs..."
      />

      <div className="flex-1 rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden flex flex-col">
        <SunbedSessionLogsTable
          logs={paginatedLogs}
          isLoading={isLoading}
          indexOfFirstItem={indexOfFirstItem}
        />

        <PaginationFooter
          totalItems={logs.length}
          filteredTotal={logs.length}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};
